import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useAppDispatch, useAppSelector } from 'store/store.hooks';
import {
  ChangeModalVisibilityActionPayload,
  changeModalVisibility,
  makeSelectModalState,
} from 'store/slices/modalSlice';
import { Dialog, Transition } from '@headlessui/react';
import Icon from 'components/Icon/Icon';
import Flex from 'style-utils/components/Flex';
import { WithFlexboxProps } from 'style-utils/withStyledProps/withFlexboxProps';
import { H3 } from 'components/Typography';
import Button from 'components/Button/Button';
import { ButtonProps } from 'components/Button/Button.types';
import { noop } from 'utils/functions/noop';
import { IconRoot } from 'components/Icon/Icon.style';
import { getThemeColor } from 'style-utils/theme/utils/getThemeColor';
import styled, { CSSObject, ThemeProvider } from 'styled-components';
import { merge } from 'style-utils/core/createExtractCssFromProps/utils/merge';
import { ReactComponent as SuccessIcon } from 'assets/icons/modals/successModal.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/modals/errorModal.svg';
import modalCloseIcon from 'assets/icons/modals/headerCloseIcon.svg';

/* -------------------------------------------------------------------------------------------------
 * STYLES
 * -----------------------------------------------------------------------------------------------*/

export type ModalTheme = {
  modalOverlay?: {
    style: CSSObject;
  };
  modalPanel?: {
    style: CSSObject;
  };
  modalContentContainer?: {
    style: CSSObject;
  };
};

const defaultModalTheme: ModalTheme = {
  modalOverlay: {
    style: {
      background: 'rgba(34, 34, 34, 0.7)',
    },
  },
  modalPanel: {
    style: {
      maxWidth: '56rem',
    },
  },
};

const createModalTheme = (customTheme: ModalTheme = {}): ModalTheme =>
  merge(defaultModalTheme, customTheme);

const ModalHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.6rem;
  ${IconRoot} {
    cursor: pointer;
  }
  background: ${getThemeColor('modalHeaderBg')};
`;

ModalHeaderWrapper.displayName = 'ModalHeaderWrapper';

const ModalContentWrapper = styled.div`
  position: fixed;
  inset: 0;
`;
ModalContentWrapper.displayName = 'ModalContentWrapper';

const ModalContentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100dvh;
  width: 100%;
  padding: 1.6rem;
  ${({ theme }: { theme: ModalTheme }) => theme.modalContentContainer?.style};
`;
ModalContentContainer.displayName = 'ModalContentContainer';

const ModalOverlay = styled(Flex)`
  position: fixed;
  inset: 0;
  ${({ theme }: { theme: ModalTheme }) => theme.modalOverlay?.style}
`;
ModalOverlay.displayName = 'ModalOverlay';

const ModalPanel = styled(Dialog.Panel)`
  width: 100%;
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  margin-top: 19.2rem;
  ${({ theme }: { theme: ModalTheme }) => theme.modalPanel?.style}
`;
ModalPanel.displayName = 'ModalPanel';

const ModalRoot = styled(Dialog)`
  position: relative;
  z-index: ${({ theme }) => theme.zIndices.modalRoot};
`;
ModalRoot.displayName = 'ModalRoot';

/* -------------------------------------------------------------------------------------------------
 * HEADER COMPONENT
 * -----------------------------------------------------------------------------------------------*/

type ModalHeaderProps = {
  i18nHeading: string;
  headingCenter?: boolean;
};

const ModalHeader = (props: ModalHeaderProps) => {
  const { i18nHeading } = props;
  const { onClose } = useModalContext();

  return (
    <ModalHeaderWrapper>
      <H3 variant="title_xl" textColor="white" i18nKey={i18nHeading} />
      <Icon onClick={onClose} iconSrc={modalCloseIcon} />
    </ModalHeaderWrapper>
  );
};

/* -------------------------------------------------------------------------------------------------
 * STATUSICON COMPONENT
 * -----------------------------------------------------------------------------------------------*/

type StatusIconProps = WithFlexboxProps & {
  isSuccess?: boolean;
  isError?: boolean;
};

const StatusIcon = (props: StatusIconProps) => {
  const { isSuccess, ...restOfProps } = props;
  const IconComponent = isSuccess ? SuccessIcon : ErrorIcon;
  return (
    <Flex justifyContent="center" w="100%" {...restOfProps}>
      <Icon iconSize="5.6rem" svgIconComponent={IconComponent} />
    </Flex>
  );
};

/* -------------------------------------------------------------------------------------------------
 * FOOTER COMPONENT
 * -----------------------------------------------------------------------------------------------*/

type ModalFooterProps = WithFlexboxProps & {
  i18nKey?: string;
  buttonProps?: Omit<ButtonProps, 'i18nKey'>;
  onClick?: () => void | Promise<void>;
};

const ModalFooter = (props: ModalFooterProps) => {
  const {
    i18nKey = 'modal.closeButton',
    buttonProps,
    onClick,
    ...flexBoxProps
  } = props;
  const { onClose } = useModalContext();

  const handleClose = () => {
    if (onClick) return onClick();
    onClose();
  };
  return (
    <Flex justifyContent="center" p="0 1.6rem 1.6rem" {...flexBoxProps}>
      <Button i18nKey={i18nKey} {...buttonProps} onClick={handleClose} />
    </Flex>
  );
};

/* -------------------------------------------------------------------------------------------------
 * MODAL CONTEXT COMPONENT
 * -----------------------------------------------------------------------------------------------*/

const ModalContext = createContext<{
  onClose: () => void | Promise<void> | null;
}>({
  onClose: noop,
});

const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
};

/* -------------------------------------------------------------------------------------------------
 * MODAL ROOT COMPONENT
 * -----------------------------------------------------------------------------------------------*/
type ModalProps = {
  modalName: string;
  modifyTheme?: ModalTheme;
  showOnMount?: boolean;
  preventCloseOnClickOutside?: boolean;
  unmountOnClose?: boolean;
  children: React.ReactNode;
  onClose?: () => void | Promise<void>;
};

const Modal = (props: ModalProps) => {
  const {
    modalName,
    modifyTheme,
    showOnMount,
    preventCloseOnClickOutside,
    unmountOnClose,
    children,
    onClose,
  } = props;

  const { showModal, hideModal, isVisible } = useModalControls(modalName);

  const modalTheme = useMemo(
    () => createModalTheme(modifyTheme),
    [modifyTheme],
  );

  const handleClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    } else {
      hideModal();
    }
  }, [onClose, hideModal]);

  useEffect(() => {
    if (showOnMount) {
      // We do not want to have this effect run everytime 'visible' changes
      // nor we can accept 'stale' value hence we intentionally
      // avoid the closure on 'visible'.
      if (!isVisible) {
        showModal();
      }
    }
  }, [showModal]);

  const contextValue = useMemo(
    () => ({
      onClose: handleClose,
    }),
    [handleClose],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      <ThemeProvider theme={modalTheme}>
        <Transition appear show={isVisible} as={Fragment}>
          <ModalRoot
            unmount={unmountOnClose}
            onClose={preventCloseOnClickOutside ? noop : handleClose}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ModalOverlay />
            </Transition.Child>
            <ModalContentWrapper>
              <ModalContentContainer>
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <ModalPanel>{children}</ModalPanel>
                </Transition.Child>
              </ModalContentContainer>
            </ModalContentWrapper>
          </ModalRoot>
        </Transition>
      </ThemeProvider>
    </ModalContext.Provider>
  );
};
/* -------------------------------------------------------------------------------------------------
 * MODAL CONTROL HOOK
 * -----------------------------------------------------------------------------------------------*/
type ModalControlHandler<T = any> = (data?: T) => {
  payload: ChangeModalVisibilityActionPayload;
  type: 'modal/changeModalVisibility';
};

type ModalControlsReturn<T = any> = [
  ModalControlHandler<T>,
  ModalControlHandler<T>,
  T,
] & {
  showModal: ModalControlHandler<T>;
  hideModal: ModalControlHandler<T>;
  modalState: T;
  isVisible: boolean;
};

const useModalControls = <T = any,>(modalName: string) => {
  const dispatch = useAppDispatch();
  const modalState = useAppSelector(makeSelectModalState(modalName));

  const showModal: ModalControlHandler<T> = useCallback(
    (data) =>
      dispatch(changeModalVisibility({ modalName, visible: true, data })),
    [modalName],
  );

  const hideModal = useCallback(
    () => dispatch(changeModalVisibility({ modalName, visible: false })),
    [modalName],
  );

  const modalControls = [
    showModal,
    hideModal,
    modalState?.data,
  ] as ModalControlsReturn<T>;

  modalControls.showModal = showModal;
  modalControls.hideModal = hideModal;
  modalControls.modalState = modalState?.data;
  modalControls.isVisible = !!modalState?.visible;

  return modalControls;
};

export { Modal, ModalHeader, ModalFooter, StatusIcon, useModalControls };
export type { ModalProps };
